import { forwardRef, type ReactNode } from 'react';

import Typography from '@dynatrace/strato-design-tokens/typography';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';
import type { TooltipBaseProps } from '../../overlays/tooltip/tooltip.types.js';
import { Text } from '../../typography/text/Text.js';
import { KeyboardShortcut } from '../keyboard-shortcut/KeyboardShortcut.js';

const COMPONENT_NAME = 'KeyboardShortcutTooltip';

// #region Typings

/**
 * Accepted properties for KeyboardShortcutTooltip
 * @public
 */
export interface KeyboardShortcutTooltipProps
  extends TooltipBaseProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * The list of keys the component should display, specified in the aria-keyshortcuts format.
   */
  keys: string;

  /**
   * Whether the component should be rendered in a disabled state.
   * @defaultValue 'false'
   */
  shortcutDisabled?: boolean;

  /** Text displayed in the tooltip component. */
  text?: ReactNode;
}
// #endregion Typings

/**
 * Use the `KeyboardShortcutTooltip` component to display a keyboard shortcut inside a tooltip.
 * @public
 */
export const KeyboardShortcutTooltip = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  KeyboardShortcutTooltipProps
>((props: KeyboardShortcutTooltipProps, forwardedRef) => {
  const {
    children,
    keys,
    shortcutDisabled,
    text,
    placement = 'top',
    open,
    defaultOpen,
    onOpenChange,
    delay = 'default',
    disabled,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  return (
    <Tooltip
      ref={forwardedRef}
      placement={placement}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delay={delay}
      disabled={disabled}
      text={
        <Flex flexDirection="column" alignItems="center" gap={4}>
          {text && (
            <Text
              textStyle="small-emphasized"
              style={{ maxWidth: '100%', overflowWrap: 'anywhere' }}
            >
              {text}
            </Text>
          )}
          <KeyboardShortcut
            keys={keys}
            variant="minimal"
            disabled={shortcutDisabled}
            style={{
              lineHeight: Typography.Text.Small.Default.LineHeight,
            }}
          />
        </Flex>
      }
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
    </Tooltip>
  );
});
(
  KeyboardShortcutTooltip as typeof KeyboardShortcutTooltip & {
    displayName: string;
  }
).displayName = COMPONENT_NAME;
